<script>
  import { authState } from '../stores/auth.js';
  import { localDBState } from '../stores/localdb.js';
  import { appStatus, pushNotice } from '../stores/ui.js';
  import { initVault, unlockVault, vaultState } from '../stores/vault.js';
  import { getClientId, setClientId, getApiKey, setApiKey, getDefaultChannelId, setDefaultChannelId } from '../lib/youtube/config.js';
  import { startOAuth } from '../lib/youtube/oauth.js';
  import { onMount } from 'svelte';

  let clientId = '';
  let apiKey = '';
  let channelId = '';
  let passphrase = '';

  onMount(async () => {
    appStatus.set({
      primary: 'Onboarding',
      secondary: 'Login → Privacy lock → Connect YouTube → First scan'
    });
    initVault();
    clientId = await getClientId() || '';
    apiKey = await getApiKey() || '';
    channelId = await getDefaultChannelId() || '';
  });

  const steps = [
    {
      title: 'Create account',
      detail: 'Register for the 14-day trial. No local agent required.'
    },
    {
      title: 'Set passphrase',
      detail: 'Derive your encryption key locally. The server never sees it.'
    },
    {
      title: 'Connect YouTube',
      detail: 'OAuth with PKCE using youtube.readonly.'
    },
    {
      title: 'First scan',
      detail: 'Fetch channel metadata and store it in IndexedDB.'
    }
  ];

  async function handleSaveClientId() {
    if (!clientId.trim()) {
      pushNotice('Please enter a client ID.', 'warning');
      return;
    }
    await setClientId(clientId);
    pushNotice('Client ID saved for this user.', 'success');
  }

  async function handleSaveApi() {
    if (apiKey) await setApiKey(apiKey);
    if (channelId) await setDefaultChannelId(channelId);
    pushNotice('API key and channel stored for this user.', 'success');
  }

  async function handleUnlockVault() {
    if (!passphrase) {
      pushNotice('Passphrase required to unlock vault.', 'warning');
      return;
    }
    await unlockVault(passphrase);
    passphrase = '';
    pushNotice('Vault unlocked for this session.', 'success');
  }

  async function handleStartOAuth() {
    try {
      await startOAuth();
    } catch (error) {
      pushNotice(error.message || 'OAuth start failed.', 'error');
    }
  }
</script>

<section class="intro">
  <div>
    <p class="eyebrow">Onboarding</p>
    <h1>Launch your private scan workflow.</h1>
    <p class="lead">Follow the steps to activate the encrypted vault and connect YouTube.</p>
  </div>
  <div class="status">
    <div>
      <div class="label">Signed in</div>
      <strong>{ $authState.isAuthenticated ? 'Yes' : 'No' }</strong>
    </div>
    <div>
      <div class="label">Vault</div>
      <strong>{ $vaultState.hasKey ? 'Unlocked' : 'Locked' }</strong>
    </div>
    <div>
      <div class="label">Local DB ready</div>
      <strong>{ $localDBState.isReady ? 'Yes' : 'No' }</strong>
    </div>
  </div>
</section>

<section class="steps">
  {#each steps as step, index}
    <div class="step">
      <div class="step-index">0{index + 1}</div>
      <div>
        <div class="step-title">{step.title}</div>
        <div class="step-detail">{step.detail}</div>
      </div>
    </div>
  {/each}
</section>

<section class="cards">
  <div class="card">
    <div class="card-title">Configure OAuth client</div>
    <p>Paste the OAuth client ID from your Google Cloud project.</p>
    <input placeholder="OAuth Client ID" bind:value={clientId} />
    <button class="secondary" on:click={handleSaveClientId}>Save client ID</button>
    <input placeholder="YouTube API Key (optional)" bind:value={apiKey} />
    <input placeholder="Channel ID for API-key scan" bind:value={channelId} />
    <button class="secondary" on:click={handleSaveApi}>Save API settings</button>
  </div>
  <div class="card">
    <div class="card-title">Unlock encryption vault</div>
    <p>Use a passphrase to unlock the local key for this session.</p>
    <input type="password" placeholder="Passphrase" bind:value={passphrase} />
    <button class="primary" on:click={handleUnlockVault}>Unlock vault</button>
  </div>
  <div class="card">
    <div class="card-title">Start PKCE OAuth</div>
    <p>Redirects to Google, then stores tokens encrypted on return.</p>
    <button class="primary" on:click={handleStartOAuth}>Connect YouTube</button>
  </div>
</section>

<style>
  .intro {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }

  h1 {
    margin: 0.5rem 0 0.75rem;
    font-size: clamp(2rem, 3vw, 2.6rem);
  }

  .lead {
    color: var(--muted);
    max-width: 520px;
  }

  .status {
    background: #fff;
    padding: 1.25rem 1.5rem;
    border-radius: 16px;
    border: 1px solid rgba(31, 41, 51, 0.1);
    display: grid;
    gap: 0.75rem;
    min-width: 220px;
  }

  .label {
    font-size: 0.75rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  .steps {
    display: grid;
    gap: 1rem;
  }

  .step {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 16px;
    border: 1px solid rgba(31, 41, 51, 0.08);
  }

  .step-index {
    font-weight: 700;
    color: var(--accent);
    font-size: 1.1rem;
  }

  .step-title {
    font-weight: 600;
  }

  .step-detail {
    color: var(--muted);
    font-size: 0.9rem;
  }

  .cards {
    margin-top: 2rem;
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .card {
    background: #fff;
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid rgba(31, 41, 51, 0.08);
    display: grid;
    gap: 0.75rem;
  }

  .card-title {
    font-weight: 700;
  }

  input {
    padding: 0.6rem 0.8rem;
    border-radius: 12px;
    border: 1px solid rgba(31, 41, 51, 0.15);
    font-size: 0.9rem;
  }



  .primary,
  .secondary {
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-weight: 600;
    width: fit-content;
  }

  .primary {
    background: var(--accent-2);
    color: #fff;
  }

  .secondary {
    background: rgba(31, 41, 51, 0.1);
  }

  @media (max-width: 820px) {
    .intro {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
